/* Reports gallery, "On screens" filter: one section per screen (laptops
 * first, then office TVs) with the reports that screen shows as small live
 * tiles, up to 6 per row -- "Fawzy laptop shows these, Management TV shows
 * those". Built from each report's `screens` (server screen reach), so it
 * matches exactly what every device is served. */
import { Laptop, Tv } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ReportPreviewThumb } from "../../components/ReportPreviewThumb";
import { Badge, EmptyState, formatRelative } from "../../ds";
import { reportUrl, type Mode } from "../../shell/role";
import type { Report, ReportScreenRef } from "../../types";
import { ONLINE_WINDOW_MS } from "../admin/labels";
import { reportStatus } from "./reportStatus";

import "./ScreensBoard.css";

interface ScreenGroup {
  screen: ReportScreenRef;
  reports: Report[];
}

function groupByScreen(reports: Report[]): ScreenGroup[] {
  const byId = new Map<string, ScreenGroup>();
  for (const r of reports) {
    for (const s of r.screens ?? []) {
      const g = byId.get(s.id) ?? { screen: s, reports: [] };
      g.reports.push(r);
      byId.set(s.id, g);
    }
  }
  const groups = [...byId.values()];
  for (const g of groups) g.reports.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  return groups.sort(
    (a, b) => Number(b.screen.laptop) - Number(a.screen.laptop) || a.screen.name.localeCompare(b.screen.name, undefined, { sensitivity: "base" }),
  );
}

const online = (s: ReportScreenRef) => !!s.last_seen_at && Date.now() - new Date(s.last_seen_at).getTime() < ONLINE_WINDOW_MS;

export function ScreensBoard({ reports, mode }: { reports: Report[]; mode: Mode }) {
  const navigate = useNavigate();
  const groups = groupByScreen(reports);
  const source = mode === "owner" ? "owner" : "admin";

  if (groups.length === 0) {
    return <EmptyState icon={Tv} title="No screen shows these reports" description="Tick a screen group or single screens under a report's Screens & access tab." />;
  }

  return (
    <div className="sb">
      {groups.map(({ screen, reports: items }) => {
        const Icon = screen.laptop ? Laptop : Tv;
        const on = online(screen);
        return (
          <section key={screen.id} className="sb-screen" aria-labelledby={`sb-${screen.id}`}>
            <header className="sb-screen__head">
              <Icon size={18} className="ds-faint" />
              <h2 id={`sb-${screen.id}`} className="sb-screen__name">{screen.name}</h2>
              <span className="ds-chip__count">{items.length}</span>
              {screen.group_name ? <span className="ds-small ds-muted">{screen.group_name}</span> : null}
              <div className="ds-grow" />
              {screen.laptop ? <span className="ds-small ds-faint">Shows the live draft</span> : null}
              <Badge tone={on ? "success" : "neutral"} dot title={screen.last_seen_at ? `Last seen ${formatRelative(screen.last_seen_at)}` : "Never seen"}>
                {on ? "Online" : screen.last_seen_at ? `Seen ${formatRelative(screen.last_seen_at)}` : "Never seen"}
              </Badge>
            </header>
            <div className="sb-grid">
              {items.map((r) => {
                const st = reportStatus(r);
                // Laptops show the draft; TVs the published version.
                const src = screen.laptop ? source : mode === "owner" ? "owner" : "published";
                return (
                  <button key={r.id} type="button" className="sb-tile" onClick={() => navigate(reportUrl(mode, r.id))} title={`${r.name} — ${st.label}`}>
                    <ReportPreviewThumb reportId={r.id} source={src} title={r.name} bleed={false} unloadOffscreen placeholder={<div className="sb-tile__ph" />} />
                    <span className="sb-tile__name ds-truncate">{r.name}</span>
                    <span className="sb-tile__meta ds-small ds-faint">
                      {r.category_name ? `${r.category_name} · ` : ""}
                      {st.kind === "draft" ? "Draft" : "Published"}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
